/**
 * @description Operations on the record data structure
 */

/**
 * @description Map a function over the values of a record
 * @param fn The function to map
 * @param record The record to map over
 * @returns A new record with the values mapped
 * @example
 * const record = { a: 'hello', b: 'world' };
 * const mapped = map(x => x.toUpperCase(), record);
 * // mapped = { a: 'HELLO', b: 'WORLD' }
 */
export const map = <K extends string | number | symbol, T, U>(
  fn: (t: T) => U,
  record: Record<K, T>
): Record<K, U> => {
  const result: Record<K, U> = {} as Record<K, U>;
  for (const key in record) {
    result[key] = fn(record[key]);
  }
  return result;
};

/**
 * @description Map a function over both the keys and values of a record
 * @param fn The function to map
 * @param record The record to map over
 * @returns A new record with the key-value pairs mapped
 * @example
 * const record = { a: 1, b: 2, c: 3 };
 * const mapped = mapWithKeys((key, val) => `${key}${val}`, record);
 * // mapped = { a: 'a1', b: 'b2', c: 'c3' }
 */
export const mapWithKeys = <K extends string | number | symbol, T, U>(
  fn: (key: K, val: T) => U,
  record: Record<K, T>
): Record<K, U> => {
  const result: Record<K, U> = {} as Record<K, U>;
  for (const key in record) {
    result[key] = fn(key, record[key]);
  }
  return result;
};

/**
 * @description Map a function over the keys of a record
 * @param fn The function to map
 * @param record The record to map over
 * @returns A new record with the keys mapped
 * @example
 * const record = { a: 1, b: 2, c: 3 };
 * const mapped = mapKeys(x => `${x}!`, record);
 * // mapped = { 'a!': 1, 'b!': 2, 'c!': 3 }
 */
export const mapKeys = <
  K1 extends string | number | symbol,
  K2 extends string | number | symbol,
  T
>(
  fn: (key: K1) => K2,
  record: Record<K1, T>
): Record<K2, T> => {
  const result: Record<K2, T> = {} as Record<K2, T>;
  for (const key in record) {
    result[fn(key)] = record[key];
  }
  return result;
};

/**
 *
 * @description Filter a record based on a predicate
 * @param fn The predicate function
 * @param record The record to filter
 * @returns A new record with the values that satisfy the predicate
 * @example
 * const record = { a: 1, b: 2, c: 3 };
 * const filtered = filter(x => x > 1, record);
 * // filtered = { b: 2, c: 3 }
 */
export const filter = <K extends string | number | symbol, T>(
  fn: (t: T) => boolean,
  record: Record<K, T>
): Record<K, T> => {
  const result: Record<K, T> = {} as Record<K, T>;
  for (const key in record) {
    if (fn(record[key])) {
      result[key] = record[key];
    }
  }
  return result;
};

/**
 * @description Filter a record based on a predicate that takes both the key and value
 * @param fn The predicate function
 * @param record The record to filter
 * @returns A new record with the key-value pairs that satisfy the predicate
 * @example
 * const record = { 1: 'a', 2: 'b', 3: 'c' };
 * const filtered = filterWithKeys((key, val) => key > 1 && val !== 'b', record);
 * // filtered = { 3: 'c' }
 * const filtered2 = filterWithKeys((key, val) => key === 1 || val === 'b', record);
 * // filtered2 = { 1: 'a', 2: 'b' }
 */
export const filterWithKeys = <K extends string | number | symbol, T>(
  fn: (key: K, val: T) => boolean,
  record: Record<K, T>
): Record<K, T> => {
  const result: Record<K, T> = {} as Record<K, T>;
  for (const key in record) {
    if (fn(key, record[key])) {
      result[key] = record[key];
    }
  }
  return result;
};

/**
 * @description Reduce a record to a single value
 * @param fn The reducer function
 * @param initial The initial value
 * @param record The record to reduce
 * @returns The reduced value
 * @example
 * const record = { a: 1, b: 2, c: 3 };
 * const sum = reduce((acc, x) => acc + x, 0, record);
 * // sum = 6
 */
export const reduce = <K extends string | number | symbol, T, U>(
  fn: (acc: U, t: T) => U,
  initial: U,
  record: Record<K, T>
): U => {
  let result = initial;
  for (const key in record) {
    result = fn(result, record[key]);
  }
  return result;
};

/**
 *
 * @description Reduce a record to a single value, with the key passed to the reducer function
 * @param fn The reducer function
 * @param initial The initial value
 * @param record The record to reduce
 * @returns The reduced value
 * @example
 * const record = { 1: 50, 2: 100, 3: 150 };
 * const sum = reduceWithKeys((acc, key, val) => key % 2 === 0 ? acc + val : acc, 0, record);
 * // sum = 100
 */
export const reduceWithKeys = <K extends string | number | symbol, T, U>(
  fn: (acc: U, key: K, val: T) => U,
  initial: U,
  record: Record<K, T>
): U => {
  let result = initial;
  for (const key in record) {
    result = fn(result, key, record[key]);
  }
  return result;
};

export const swapKeysAndValues = <
  K extends string | number | symbol,
  T extends string | number | symbol
>(
  record: Record<K, T>
): Record<T, K> => {
  const result: Record<T, K> = {} as Record<T, K>;
  for (const [key, value] of entries(record)) {
    result[value] = key;
  }
  return result;
};

/**
 * @description Find the first value in a record that satisfies a predicate
 * @param fn The predicate function
 * @param record The record to search
 * @returns The first value that satisfies the predicate, or undefined if none do
 * @example
 * const record = { a: 1, b: 2, c: 3 };
 * const found = find(x => x > 1, record);
 * // found = 2
 * const notFound = find(x => x > 3, record);
 * // notFound = undefined
 */
export const find = <K extends string | number | symbol, T>(
  fn: (t: T) => boolean,
  record: Record<K, T>
): T | undefined => {
  for (const key in record) {
    if (fn(record[key])) {
      return record[key];
    }
  }
  return undefined;
};

/**
 *
 * @description Find the first value in a record that satisfies a predicate
 * @param fn The predicate function
 * @param record The record to search
 * @returns The first value that satisfies the predicate, or undefined if none do
 * @example
 * const record = { 1: 'a', 2: 'b', 3: 'c' };
 * const found = findWithKeys((key, _) => key > 1, record);
 * // found = 'b'
 */
export const findWithKeys = <K extends string | number | symbol, T>(
  fn: (key: K, val: T) => boolean,
  record: Record<K, T>
): T | undefined => {
  for (const key in record) {
    if (fn(key, record[key])) {
      return record[key];
    }
  }
  return undefined;
};

/**
 *
 * @description Find the first key in a record that satisfies a predicate on the key
 */
export const findKeyWithKeys = <K extends string | number | symbol, T>(
  fn: (key: K, val: T) => boolean,
  record: Record<K, T>
): K | undefined => {
  for (const key in record) {
    if (fn(key, record[key])) {
      return key;
    }
  }
  return undefined;
};

/**
 * @description Find the first key in a record that satisfies a predicate
 * @param fn The predicate function
 * @param record The record to search
 * @returns The first key that satisfies the predicate, or undefined if none do
 * @example
 * const record = { a: 1, b: 2, c: 3 };
 * const found = findKey(x => x > 1, record);
 * // found = 'b'
 */
export const findKey = <K extends string | number | symbol, T>(
  fn: (t: T) => boolean,
  record: Record<K, T>
): K | undefined => {
  for (const key in record) {
    if (fn(record[key])) {
      return key;
    }
  }
  return undefined;
};

export const findEntry = <K extends string | number | symbol, T>(
  fn: (key: K, val: T) => boolean,
  record: Record<K, T>
): [K, T] | undefined => {
  for (const [key, val] of Object.entries(record) as [K, T][]) {
    if (fn(key, val)) {
      return [key, val];
    }
  }
  return undefined;
};

export const hasKey = <K extends string | number | symbol, T>(
  key: K,
  record: Record<K, T>
): boolean => {
  return findKeyWithKeys((k) => k === key, record) !== undefined;
};
/**
 *
 * @description Perform a zip operation on two records
 * @param record1 The first record
 * @param record2 The second record
 * @returns A new record with the keys that are present in both input records, and the values as a tuple
 * @example
 * const record1 = { a: 1, b: 2, c: 3 };
 * const record2 = { a: 'x', b: 'y', d: 'z' };
 * const zipped = zip(record1, record2);
 * // zipped = { a: [1, 'x'], b: [2, 'y'] }
 */
export const zip = <K extends string | number | symbol, T, U>(
  record1: Record<K, T>,
  record2: Record<K, U>
): Record<K, [T, U]> => {
  const result: Record<K, [T, U]> = {} as Record<K, [T, U]>;
  for (const key in record1) {
    if (key in record2) {
      result[key] = [record1[key], record2[key]];
    }
  }
  return result;
};

export const zipToRecord = <K extends string | number | symbol, T>(
  keys: K[],
  values: T[]
): Record<K, T> => {
  const result: Record<K, T> = {} as Record<K, T>;
  for (let i = 0; i < keys.length; i++) {
    if (values[i] === undefined) {
      continue;
    }
    result[keys[i]!] = values[i]!;
  }
  return result;
};

/**
 * @description Get a new record with all entries of record1 that are not also in record2
 * @example
 * const record1 = { a: 1, b: 2, c: 3 };
 * const record2 = { a: 1, b: 2, d: 4 };
 * const diff = getDiff(record1, record2);
 * // diff = { c: 3 }
 */
export const getDiff = <
  K1 extends string | number | symbol,
  K2 extends string | number | symbol,
  K3 extends string | number | symbol,
  T
>(
  record1: Record<K1, T>,
  record2: Record<K2, T>
): Record<K3, T> => {
  const result: Record<K3, T> = {} as Record<K3, T>;
  for (const key in record1) {
    if (record1[key] !== record2[key as unknown as K2]) {
      result[key as unknown as K3] = record1[key];
    }
  }
  return result;
};

export const toArray = <K extends string | number, T>(
  record: Record<K, T>
): [K, T][] => {
  return Object.entries(record) as [K, T][];
};

export const fromArray = <K extends string | number | symbol, T>(
  array: [K, T][]
): Record<K, T> => {
  return array.reduce(
    (acc, [key, value]) => ({ ...acc, [key]: value }),
    {} as Record<K, T>
  );
};

export const values = <K extends string | number | symbol, T>(
  record: Record<K, T>
): T[] => {
  return Object.values(record);
};

export const keys = <K extends string | number | symbol, T>(
  record: Record<K, T>
): K[] => {
  return Object.keys(record) as K[];
};

export const entries = <K extends string | number | symbol, T>(
  record: Record<K, T>
): [K, T][] => {
  return Object.entries(record) as [K, T][];
};

export const length = <K extends string | number | symbol, T>(
  record: Record<K, T>
): number => {
  return Object.keys(record).length;
};
